import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useToast, type ToastItem } from './useToast'

function ToastItemComponent({ toast, onRemove }: { toast: ToastItem; onRemove: (id: string) => void }) {
  const { t } = useTranslation('components')
  const [isExiting, setIsExiting] = useState(false)

  function handleClose() {
    setIsExiting(true)
    setTimeout(() => {
      onRemove(toast.id)
    }, 150) // wait for fade-out transition
  }

  useEffect(() => {
    const duration = toast.duration ?? 5000
    const timer = setTimeout(() => {
      handleClose()
    }, duration)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast])

  const icons = {
    success: <CheckCircle className="toast-icon" />,
    error: <XCircle className="toast-icon" />,
    info: <Info className="toast-icon" />,
    warning: <AlertTriangle className="toast-icon" />
  }

  return (
    <div
      className={`toast toast--${toast.type}`}
      role="alert"
      style={{ opacity: isExiting ? 0 : 1, transform: isExiting ? 'scale(0.95)' : 'none' }}
    >
      {icons[toast.type]}
      <div className="toast-content">{toast.message}</div>
      <button className="toast-close" onClick={handleClose} aria-label={t('toast.closeAriaLabel')}>
        <X size={16} />
      </button>
    </div>
  )
}

export function ToastContainer() {
  const { toasts, removeToast } = useToast()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true)
  }, [])

  if (!mounted || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div className="toast-stack">
      {toasts.map((t) => (
        <ToastItemComponent key={t.id} toast={t} onRemove={removeToast} />
      ))}
    </div>,
    document.body
  )
}
